import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import socketService from '../services/socketService';
import apiService from '../services/apiService';
import { useAuthStore } from '../stores/authStore';
import { useNotificationStore } from '../stores/notificationStore';
import { tokenStorage } from '../lib/tokenStorage';
import type { InAppNotification } from '../stores/notificationStore';

/**
 * Global socket connection — mount once in MainLayout.
 * Connects on login, listens for personal notifications, disconnects on logout.
 */
export function useGlobalSocket() {
  const isAuthenticated = useAuthStore((s) => !!s.user);
  // Use tokenStorage as source of truth (stays fresh after refresh)
  const storeToken = useAuthStore((s) => s.token);
  const addNotification = useNotificationStore((s) => s.addNotification);
  const setNotifications = useNotificationStore((s) => s.setNotifications);
  const isLoaded = useNotificationStore((s) => s.isLoaded);
  const queryClient = useQueryClient();
  const initializedRef = useRef(false);

  // Load notifications from API on first mount
  useEffect(() => {
    if (!isAuthenticated || isLoaded) return;

    apiService.getNotifications(1, 30).then((data) => {
      const notifications = data?.notifications || [];
      const unreadCount = data?.unreadCount ?? notifications.filter((n: any) => !n.read).length;
      setNotifications(notifications, unreadCount);
    }).catch((err) => {
      console.error('[Notifications] Failed to load:', err.message);
      // Set empty to prevent retrying
      setNotifications([], 0);
    });
  }, [isAuthenticated, isLoaded, setNotifications]);

  // Socket connection + event listener
  useEffect(() => {
    const token = tokenStorage.getAccessToken() || storeToken;
    if (!isAuthenticated || !token) {
      if (initializedRef.current) {
        socketService.disconnect();
        initializedRef.current = false;
      }
      return;
    }

    // Pass a provider so reconnects pick up refreshed tokens instead of
    // replaying the stale one from the initial handshake.
    const socket = socketService.connect(() => tokenStorage.getAccessToken() || storeToken || null);
    initializedRef.current = true;

    const handleNotification = (data: InAppNotification) => {
      console.log('[Socket] Notification received:', data.type, data.title);

      // Points updates during live play are noisy — per-tick notifications
      // during a match don't help. Only surface points_updated once the match
      // is fully complete (MoM included), flagged by metadata.live === false.
      // Fall back to showing it when `live` is absent (back-compat).
      const isLiveTick =
        data.type === 'points_updated' && data.metadata?.live === true;

      if (!isLiveTick) {
        addNotification(data);
        toast.info(data.message, {
          autoClose: 4000,
        });
      }

      // Invalidate relevant queries for fresh data
      if (data.contestId) {
        if (['member_joined', 'member_accepted', 'join_request'].includes(data.type)) {
          queryClient.invalidateQueries({ queryKey: ['contests', data.contestId, 'members'] });
          queryClient.invalidateQueries({ queryKey: ['contests', data.contestId] });
        }
        if (['contest_locked', 'contest_unlocked'].includes(data.type)) {
          queryClient.invalidateQueries({ queryKey: ['contests', data.contestId, 'lock-status'] });
        }
        if (data.type === 'points_updated') {
          queryClient.invalidateQueries({ queryKey: ['leaderboard', data.contestId] });
          queryClient.invalidateQueries({ queryKey: ['fantasy-points', data.contestId] });
        }
        if (data.type === 'transfer_completed') {
          queryClient.invalidateQueries({ queryKey: ['transfer-status', data.contestId] });
          queryClient.invalidateQueries({ queryKey: ['teams', data.contestId] });
        }
        // Team created / general contest updates — refresh teams & contest data
        if (data.type === 'general') {
          queryClient.invalidateQueries({ queryKey: ['teams', data.contestId] });
          queryClient.invalidateQueries({ queryKey: ['contests', data.contestId] });
        }
      }

      // Refresh contests list for join-related events
      if (['member_accepted', 'member_rejected', 'member_removed'].includes(data.type)) {
        queryClient.invalidateQueries({ queryKey: ['contests', 'user'] });
        queryClient.invalidateQueries({ queryKey: ['contests'] });
      }
    };

    socket.on('notification:personal', handleNotification);

    return () => {
      socket.off('notification:personal', handleNotification);
    };
  }, [isAuthenticated, storeToken, addNotification, queryClient]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (initializedRef.current) {
        socketService.disconnect();
        initializedRef.current = false;
      }
    };
  }, []);
}
